import React from "react";
import { FaUsers, FaCalendarAlt } from "react-icons/fa";
import { FaListCheck } from "react-icons/fa6";
import {
  Users,
  Store,
  BarChart3,
  GraduationCap,
  UserCheck,
  LogIn,
  TrendingDown,
  DollarSign,
  Target
} from "lucide-react";

export default function StatCard({
  title,
  value,
  icon,
  trend,
  subtext,
  loading,
  variant = "default", // "default" or "admin"
  color = "burgundy",
}) {
  const colorMap = {
    red: { bg: "bg-rose-50", text: "text-rose-500" },
    green: { bg: "bg-emerald-50", text: "text-emerald-500" },
    blue: { bg: "bg-blue-50", text: "text-blue-500" },
    purple: { bg: "bg-purple-50", text: "text-purple-500" },
    indigo: { bg: "bg-indigo-50", text: "text-indigo-500" },
    yellow: { bg: "bg-amber-50", text: "text-amber-500" },
    orange: { bg: "bg-orange-50", text: "text-orange-500" },
    teal: { bg: "bg-teal-50", text: "text-teal-500" },
    burgundy: { bg: "bg-[#881337]/5", text: "text-[#881337]" },
    slate: { bg: "bg-slate-50", text: "text-slate-500" },
  };

  const selectedColor = colorMap[color] || colorMap.burgundy;

  if (loading) {
    return (
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 h-full flex flex-col justify-between animate-pulse">
        <div className="flex justify-between items-start mb-4">
          <div className="h-4 bg-gray-100 rounded w-2/3"></div>
          <div className="h-5 w-5 bg-gray-100 rounded"></div>
        </div>
        <div>
          <div className="h-10 bg-gray-100 rounded w-1/2 mb-2"></div>
          <div className="h-3 bg-gray-100 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (variant === "admin") {
    return (
      <div className="bg-white p-6 rounded-[20px] shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 h-full flex flex-col justify-between group">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-[13px] font-bold text-gray-500 tracking-tight leading-relaxed max-w-[75%] text-left uppercase whitespace-nowrap overflow-hidden text-ellipsis">
            {title}
          </h3>
          <div className={`${selectedColor.bg} ${selectedColor.text} p-2.5 rounded-xl transition-transform duration-300 group-hover:scale-110 flex items-center justify-center min-w-[40px] min-h-[40px]`}>
            {React.isValidElement(icon) ? icon : (
              <div className="w-5 h-5 flex items-center justify-center">
                {icon === "users" && <Users size={20} />}
                {icon === "store" && <Store size={20} />}
                {icon === "chart" && <BarChart3 size={20} />}
                {icon === "graduation" && <GraduationCap size={20} />}
                {icon === "user-check" && <UserCheck size={20} />}
                {icon === "login" && <LogIn size={20} />}
                {icon === "trending-down" && <TrendingDown size={20} />}
                {icon === "dollar" && <DollarSign size={20} />}
                {!['users', 'store', 'chart', 'graduation', 'user-check', 'login', 'trending-down', 'dollar'].includes(icon) && <Target size={20} />}
              </div>
            )}
          </div>
        </div>

        <div className="text-left mt-auto">
          <div className="flex items-baseline gap-2">
            <span className="text-[32px] font-semibold text-[#111827] tracking-tighter leading-none">
              {value}
            </span>
            {trend && (
              <span className={`text-xs font-bold ${trend === "up" ? "text-emerald-500" : "text-rose-500"} flex items-center`}>
                {trend === "up" ? "↑" : "↓"}
              </span>
            )}
          </div>
          {subtext && (
            <p className="text-[12px] text-gray-400 mt-1 font-medium tracking-tight">
              {subtext}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200 h-full flex flex-col justify-between">
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-sm font-medium text-start text-gray-500 leading-snug w-[80%]">
          {title}
        </h3>
        <div className="text-gray-400 shrink-0">
          {React.isValidElement(icon) ? icon : (
            <>
              {icon === "users" && <FaUsers />}
              {icon === "calendar" && <FaCalendarAlt />}
              {icon === "list-check" && <FaListCheck />}
              {/* Fallback for other strings */}
              {!['users', 'calendar', 'list-check'].includes(icon) && icon}
            </>
          )}
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-baseline">
          <span className={`text-3xl font-bold ${icon === "exclamation-triangle" ? "text-red-500" : "text-gray-800"} tracking-tight`}>
            {value}
          </span>
          {trend && (
            <span
              className={`ml-2 text-xs font-medium ${trend === "up" ? "text-green-500" : "text-red-500"
                } flex items-center`}
            >
              {trend === "up" ? "↑" : "↓"}
            </span>
          )}
        </div>
        {subtext && (
          <p className="text-xs text-gray-400 mt-1 font-medium">{subtext}</p>
        )}
      </div>
    </div>
  );
}
